import * as React from 'react'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as EDLStore from '@store/edl/edl'

import { EtatDesLieuxTypeEnum } from '@api/enum'

import sanitize from '@utils/Sanitizer'
import { isBefore, isAfter, addDays, isToday } from 'date-fns'

/**
 * Manifestation IN rule
 * @param manif manifestation
 * @returns boolean -> is IN
 */
export const isManifestationIN = (manif: ManifestationResponse) => {
  const now = Date.now()
  return (
    isAfter(now, addDays(new Date(manif.dateDebutMontage), -15)) && isBefore(now, new Date(manif.dateFermeturePublic))
  )
}

/**
 * Manifestation OUT rule
 * @param manif manifestation
 * @returns boolean -> is OUT
 */
export const isManifestationOUT = (manif: ManifestationResponse) => {
  const now = Date.now()
  return (
    isToday(new Date(manif.dateFermeturePublic)) ||
    (isAfter(now, new Date(manif.dateFermeturePublic)) && isBefore(now, addDays(new Date(manif.dateFinDemontage), 15)))
  )
}

/**
 * Tenant IN rule
 * @param tenant tenant
 * @returns boolean -> is IN
 */
export const isTenantIN = (tenant: Tenant) => {
  const now = Date.now()

  return (
    !!tenant.dates.start &&
    isAfter(now, addDays(new Date(tenant.dates.start), -15)) &&
    isBefore(now, addDays(new Date(tenant.dates.start), 7))
  )
}

/**
 * Tenant OUT rule
 * @param tenant tenant
 * @returns boolean -> is OUT
 */
export const isTenantOUT = (tenant: Tenant) => {
  const now = Date.now()

  return (
    !!tenant.dates.end &&
    isAfter(now, addDays(new Date(tenant.dates.end), -7)) &&
    isBefore(now, addDays(new Date(tenant.dates.end), 15))
  )
}

/**
 * Filter manifestation list based on given filters and search
 * @param manifestations initial list
 * @param filters IN/OUT  -  MANIF/TENANT
 * @returns filtered list
 */
const filterManifestations = (
  manifestations: OrderedManifestations,
  filters: EDLFilter[]
): ManifestationEDLListResponse[] => {
  if (filters.includes('TENANT') && !filters.includes('MANIFESTATION')) {
    // only tenants filter
    return []
  }

  const all = [...manifestations.inProgress, ...manifestations.incoming, ...manifestations.toBeClosed]
  const IN = all.filter((m) => isManifestationIN(m) && !isManifestationOUT(m))
  const OUT = all.filter((m) => isManifestationOUT(m) && !isManifestationIN(m))
  const INANDOUT = all
    .filter((m) => isManifestationOUT(m) && isManifestationIN(m))
    .reduce((acc, cur) => {
      acc.push(cur)
      acc.push({ ...cur, isOut: true })
      return acc
    }, [] as ManifestationEDLListResponse[])

  if ((!filters.includes('IN') && !filters.includes('OUT')) || (filters.includes('IN') && filters.includes('OUT'))) {
    // no filter on in-out
    return [...INANDOUT, ...IN, ...OUT]
  } else {
    if (filters.includes('IN')) {
      // IN -> Date de montage – 15jours < aujourd’hui < date de fermeture au public
      return [...INANDOUT.filter((m) => !m.isOut), ...IN]
    } else {
      // OUT -> Date de fermeture au public < aujourd’hui < Date de démontage + 15 jours
      return [...INANDOUT.filter((m) => m.isOut), ...OUT]
    }
  }
}

/**
 * Filter tenants list based on given filters and search
 * @param tenants initial list
 * @param filters IN/OUT  -  MANIF/TENANT
 * @returns filtered list
 */
const filterTenants = (tenants: OrderedTenants, filters: EDLFilter[]) => {
  if (filters.includes('MANIFESTATION') && !filters.includes('TENANT')) {
    // only manifs filter
    return []
  }

  const all = [...tenants.current, ...tenants.former, ...tenants.incoming]
  const IN = all.filter((t) => isTenantIN(t))
  const OUT = all.filter((t) => isTenantOUT(t))

  if ((!filters.includes('IN') && !filters.includes('OUT')) || (filters.includes('IN') && filters.includes('OUT'))) {
    // no filter on in-out
    return [...IN, ...OUT]
  } else {
    if (filters.includes('IN')) {
      // IN -> Date d’entrée – 15 jours < aujourd’hui < Date d’entrée + 7j
      return IN
    } else {
      // OUT -> Date de sortie – 7 j <Aujourd’hui < Date de sortie + 15 jours
      return OUT
    }
  }
}

/**
 * Filter completed edl list based on given filters and search
 * @param EDLs initial list
 * @param manifestations manifestations list
 * @param tenants tenants list
 * @param filters IN/OUT  -  MANIF/TENANT
 * @param group indicates tab selection
 * @returns filtered list
 */
const filterEDL = (EDLs: EtatDesLieuxResponse[], filters: EDLFilter[], group: EDLGroup) => {
  if (group !== 'finished') {
    // EDLs are only in finished state
    return []
  }

  const typeFiltered = filters.includes('OUT')
    ? filters.includes('IN')
      ? EDLs // both filter
      : EDLs.filter((edl) => edl.type === EtatDesLieuxTypeEnum.SORTIE) // only out
    : filters.includes('IN')
    ? EDLs.filter((edl) => edl.type === EtatDesLieuxTypeEnum.ENTREE) // only in
    : EDLs // no filter

  const originFiltered = filters.includes('MANIFESTATION')
    ? filters.includes('TENANT')
      ? typeFiltered // both filter
      : typeFiltered.filter((edl) => !!edl.manifestationId) // only manif
    : filters.includes('TENANT')
    ? typeFiltered.filter((edl) => !!edl.locatairePermanentId) // only tenant
    : typeFiltered // no filter

  return originFiltered
}

/**
 * Filter in progress form list based on given filters and search
 * @param EDLs initial list
 * @param filters IN/OUT  -  MANIF/TENANT
 * @param group indicates tab selection
 * @returns filtered list
 */
const filterForms = (EDLs: EDLForm[], filters: EDLFilter[], group: EDLGroup) => {
  if (group !== 'inProgress') {
    // EDLs are only in finished state
    return []
  }

  const typeFiltered = filters.includes('OUT')
    ? filters.includes('IN')
      ? EDLs // both filter
      : EDLs.filter((edl) => edl.type === EtatDesLieuxTypeEnum.SORTIE) // only out
    : filters.includes('IN')
    ? EDLs.filter((edl) => edl.type === EtatDesLieuxTypeEnum.ENTREE) // only in
    : EDLs // no filter

  const originFiltered = filters.includes('MANIFESTATION')
    ? filters.includes('TENANT')
      ? typeFiltered // both filter
      : typeFiltered.filter((edl) => !!edl.manifestationId) // only manif
    : filters.includes('TENANT')
    ? typeFiltered.filter((edl) => !!edl.locatairePermanentId) // only tenant
    : typeFiltered // no filter

  return originFiltered
}

/**
 * Find given id manifestation and apply search
 * @param manifestations manifestation list
 * @param search term to search
 * @param id id to find
 * @returns boolean is search present
 */
const searchInManifestation = (manifestations: OrderedManifestations, search: string, id?: string) => {
  if (!id) {
    return false
  }

  const all = [...manifestations.inProgress, ...manifestations.incoming, ...manifestations.toBeClosed]
  const manif = all.find((m) => m.id === id)

  if (manif) {
    return sanitize(manif.libelle).includes(search)
  }

  // no associated data -> filter
  return false
}

/**
 * Find given id tenant and apply search
 * @param manifestations tenant list
 * @param search term to search
 * @param id id to find
 * @returns boolean is search present
 */
const searchInTenant = (tenants: OrderedTenants, search: string, id?: string) => {
  if (!id) {
    return false
  }

  const all = [...tenants.current, ...tenants.former, ...tenants.incoming]
  const tenant = all.find((t) => (t.tenant.id = id))

  if (tenant) {
    return sanitize(tenant.tenant.nomLocatairePermanent).includes(search)
  }

  // no associated data -> filter
  return false
}

const useEDL = (filters: EDLFilter[], search: string, group: EDLGroup) => {
  const term = React.useMemo(() => sanitize(search), [search])

  // *****************************************************
  //                    INITIAL LISTS
  // *****************************************************
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const tenants = useReducer(SiteStore.store, (s) => s.tenants)
  const EDLs = useReducer(EDLStore.store, (s) => s.EDLs)
  const forms = useReducer(EDLStore.store, (s) => s.forms)

  // *****************************************************
  //                   FILTERED LISTS
  // *****************************************************
  const filteredManifs = React.useMemo(() => filterManifestations(manifestations, filters), [manifestations, filters])
  const filteredTenants = React.useMemo(() => filterTenants(tenants, filters), [tenants, filters])
  const filteredEDLs = React.useMemo(() => filterEDL(EDLs, filters, group), [EDLs, filters, group])
  const filteredForms = React.useMemo(() => filterForms(forms, filters, group), [forms, filters, group])

  // *****************************************************
  //                   FILTERED WITH SEARCH
  // *****************************************************
  const searchedManifestations = React.useMemo(
    () => filteredManifs.filter((m) => sanitize(m.libelle).includes(term)),
    [filteredManifs, term]
  )

  const searchedTenants = React.useMemo(
    () => filteredTenants.filter((t) => sanitize(t.tenant.nomLocatairePermanent).includes(term)),
    [filteredTenants, term]
  )

  const searchedEDLs = React.useMemo(
    () =>
      filteredEDLs.filter(
        (edl) =>
          searchInManifestation(manifestations, term, edl.manifestationId) ||
          searchInTenant(tenants, term, edl.locatairePermanentId)
      ),
    [search, manifestations, tenants, filteredEDLs]
  )

  const searchedForms = React.useMemo(
    () =>
      filteredForms
        .filter(
          (f) =>
            searchInManifestation(manifestations, term, f.manifestationId) ||
            searchInTenant(tenants, term, f.locatairePermanentId)
        )
        .sort((a, b) => b.dateDeCreation.localeCompare(a.dateDeCreation)),
    [search, manifestations, tenants, filteredForms]
  )

  return [
    searchedManifestations,
    searchedTenants,
    searchedEDLs,
    searchedForms,
    [...manifestations.inProgress, ...manifestations.incoming, ...manifestations.toBeClosed],
    [...tenants.current, ...tenants.former, ...tenants.incoming],
    EDLs,
  ] as const
}

export default useEDL
